import ValidationCheck from '@/components/atom/icon/ValidationCheck';
import ClickLogger from '@/components/common/ClickLogger';
import { EWebTreatmentRequestLog } from '@/entities/diagnosis/libs';
import { EDoctorSortType } from '@/models/doctor/telemedicine';
import styled, { css } from 'styled-components';
import { Drawer } from 'vaul';

interface Props {
  open: boolean;
  selectedSortKey: EDoctorSortType;
  onClickClose: () => void;
  handleSearchDoctorSortFilterSelect: (sortKey: DOCTOR_SORT_KEY) => void;
}

const DOCTOR_SORT_KEY = {
  DEFAULT: {
    id: 0,
    value: EDoctorSortType.DEFAULT,
    name: '기본순',
    toastMessage: '별점, 응답시간 등을 고려한 추천 순으로 정렬했어요.',
  },
  REVIEW_COUNT: {
    id: 1,
    value: EDoctorSortType.REVIEW_COUNT,
    name: '후기많은순',
    toastMessage: '후기가 많은 순으로 정렬했어요.',
  },
  REVIEW_AVERAGE: {
    id: 2,
    value: EDoctorSortType.REVIEW_AVERAGE_RATING,
    name: '별점높은순',
    toastMessage: '별점이 높은 순으로 정렬했어요.',
  },
  DISTANCE: {
    id: 3,
    value: EDoctorSortType.DISTANCE,
    name: '가까운순',
    toastMessage: '현재 위치와 가까운 순으로 정렬했어요.',
  },
} as const;

export type DOCTOR_SORT_KEY = (typeof DOCTOR_SORT_KEY)[keyof typeof DOCTOR_SORT_KEY];

export const DOCTOR_SORT_KEY_LIST = Object.values(DOCTOR_SORT_KEY);

const SearchDoctorSortFilterBottomSheet = ({
  open,
  selectedSortKey,
  onClickClose,
  handleSearchDoctorSortFilterSelect,
}: Props) => {
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClickClose();
    }
  };

  return (
    <Drawer.Root modal open={open} onOpenChange={handleOpenChange} dismissible>
      <Drawer.Portal>
        <StyledOverlay />
        <Content>
          <Drawer.Handle className='mx-auto mb-[16px] h-[4px] w-[48px] flex-shrink-0 rounded-full bg-gray-300' />
          <Title>정렬 기준을 선택해주세요</Title>
          <ListWrapper>
            {DOCTOR_SORT_KEY_LIST.map((item) => (
              <ClickLogger
                key={item.id}
                params={{
                  eventName: EWebTreatmentRequestLog.ClickDoctorSort,
                  payload: { sort_type: item.value },
                }}
              >
                <SortItem onClick={() => handleSearchDoctorSortFilterSelect(item)}>
                  <span>{item.name}</span>
                  {selectedSortKey === item.value && <ValidationCheck size={22} />}
                </SortItem>
              </ClickLogger>
            ))}
          </ListWrapper>
        </Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default SearchDoctorSortFilterBottomSheet;

const StyledOverlay = styled(Drawer.Overlay)`
  position: fixed;
  inset: 0;
  z-index: 260;
  margin: 0 auto;
  max-width: 768px;
  background-color: rgba(0, 0, 0, 0.4);
`;

const Content = styled(Drawer.Content)`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 261;
  margin: 0 auto;
  max-width: 768px;
  background-color: ${({ theme }) => theme.color.white};
  border-radius: 10px 10px 0 0;
  padding: 8px 20px 20px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0px -2px 8px 0px rgba(0, 0, 0, 0.16);

  &:focus-visible {
    outline: none;
  }
`;

const Title = styled.h2`
  ${({ theme }) => css`
    ${theme.typography.UI22SB}
    color: ${theme.color.gray900};
    margin-bottom: 20px;
    padding: 0 4px;
  `}
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SortItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0;
  cursor: pointer;
  transition: background-color 0.2s ease;

  ${({ theme }) => css`
    ${theme.typography.UI16R}
    color: ${theme.color.gray900};

    &:hover {
      background-color: ${theme.color.gray20};
    }
  `}
`;
